import React from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import { useCurrency } from '../../context/LocaleContext';
import {
  normaliseDuration, stripMinus, templateFormErrors,
} from '../../domain/packageTemplates';
import s from './packages.module.css';
import { DecimalInput } from '../RegionInputs';

// `Add Package Template` / `Edit Package Template`.
//
// Field for field and sentence for sentence as MMS's; the table of what is
// required and what each refusal says is at the head of
// `src/domain/packageTemplates.js`. Three things a reader might take for
// bugs:
//
//   * Duration does not refuse an out-of-range minutes box: it normalises on
//     blur, `90` becoming `1` hour and `30` minutes. `0 h 0 m` is the one
//     refusal, and the only thing `Please enter an amount greater than 0` is
//     shown for.
//   * Price drops a typed minus silently, at the keystroke, as MMS's does on
//     this box and on its expense `Amount` (#166, #173); see the domain header.
//   * `Status` is a radio: it is what a soft delete sets.
//
// Save is not disabled while the form is wrong: the errors sit under the boxes
// and Save refuses (#164, #171), so the field at fault is named.
export default function PackageTemplateDrawer({
  open, form, onChange, onClose, onSave, saving = false, editing = false,
}) {
  const currency = useCurrency();
  // The magnitude rule, alongside the required one. `templateErrors` says
  // whether the box is empty; `amountError` says whether what is in it is a
  // storable amount in this studio's currency (#164). Two questions, and the
  // teacher should be told whichever is wrong — `templateFormErrors` asks both.
  const errors = templateFormErrors(form, currency);
  const priceError = errors.price;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Package Template' : 'Add Package Template'}
      width={560}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <FooterSpacer />
          <Button onClick={onSave} loading={saving}>Save</Button>
        </>
      )}
    >
      <Field label="Package Template Name" error={errors.name} required>
        <input
          type="text"
          className={s.input}
          value={form.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </Field>

      <div className={s.section}>
        <Field label="Description" hint="Optional">
          <textarea
            className={s.textarea}
            value={form.description}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </Field>
      </div>

      <div className={s.section}>
        {/* One `Field` around both boxes, because MMS shows one label and one
            error for the pair — the refusal is about the *duration*, not about
            the hours box or the minutes box. */}
        <Field label="Duration" error={errors.duration} required>
          <div className={s.duration}>
            <div className={s.durationBox}>
              <input
                type="number"
                min="0"
                className={s.input}
                value={form.hours}
                onChange={(e) => onChange({ hours: e.target.value })}
                onBlur={() => onChange(normaliseDuration(form.hours, form.minutes))}
                aria-label="Duration hours"
              />
            </div>
            <span className={s.unit}>hours</span>
            <div className={s.durationBox}>
              <input
                type="number"
                min="0"
                className={s.input}
                value={form.minutes}
                onChange={(e) => onChange({ minutes: e.target.value })}
                // The normalisation, on blur, exactly where MMS does it.
                onBlur={() => onChange(normaliseDuration(form.hours, form.minutes))}
                aria-label="Duration minutes"
              />
            </div>
            <span className={s.unit}>minutes</span>
          </div>
        </Field>
      </div>

      <div className={s.narrow}>
        <Field label="Price" error={priceError} required>
          <DecimalInput
            className={s.input}
            aria-label="Price"
            value={form.price}
            // `stripMinus` is the keystroke behaviour, not a save-time clamp:
            // the character never survives the field, so `-50` reads `50` while
            // the teacher is still typing. It also applies the currency's
            // decimal places (#308).
            onChange={(e) => onChange({ price: stripMinus(e.target.value, currency) })}
          />
        </Field>
      </div>

      <div className={s.section}>
        <Field label="Status">
          <div className={s.radioRow}>
            <label className={s.radio}>
              <input
                type="radio"
                name="package-template-status"
                checked={form.status !== 'inactive'}
                onChange={() => onChange({ status: 'active' })}
              />
              Active
            </label>
            <label className={s.radio}>
              <input
                type="radio"
                name="package-template-status"
                checked={form.status === 'inactive'}
                onChange={() => onChange({ status: 'inactive' })}
              />
              Inactive
            </label>
          </div>
        </Field>
      </div>

      <label className={s.toggleRow}>
        <input
          type="checkbox"
          checked={Boolean(form.expires)}
          onChange={(e) => onChange({ expires: e.target.checked })}
        />
        Package Expiry
      </label>

      {/* One field, revealed by the toggle, as MMS reveals it. The label is
          MMS's verbatim because the days run from the sale's start date, not
          from the day of sale: a 30-day template sold to start 9/17 expires
          10/17, and backdated to 9/1 expires 10/1. */}
      {form.expires ? (
        <div className={s.narrow}>
          <Field label="Days to Expire After Start Date" error={errors.expiryDays} required>
            <input
              type="number"
              min="1"
              className={s.input}
              value={form.expiryDays}
              onChange={(e) => onChange({ expiryDays: e.target.value })}
            />
          </Field>
        </div>
      ) : null}

      {/* The remainder is forfeited at expiry, as in MMS (#368). The order a
          lesson draws in is ours rather than MMS's (`core/src/packages.rs`),
          and it is named because a teacher setting an expiry date on one of
          two templates is the person it affects. */}
      {form.expires ? (
        <div className={s.hint}>
          The expiry date is recorded against each sale, counted from that sale’s start date.
          Time left in a package on that date is <b>forfeited</b> — and a lesson draws on
          whichever of a student’s packages expires soonest.
        </div>
      ) : null}
    </Drawer>
  );
}
